import React, { memo, useEffect, useState } from 'react';
import { ArrayTwoOrMore, Channel, ChannelFilters, ChannelSort } from 'stream-chat';
import { ChatListViewHeader } from './ChatListViewHeader';
import { ChannelList, InfiniteScroll } from 'stream-chat-react';
import { useAuth } from '../../../hooks/useAuth';
import { ChatFilter } from '../../../models/ChatFilter';
import { ChatListTile } from './ChatListTile';
import { createChatChannelModel } from '../../../utils/ChatChannelUtils';
import Loader from './Loader';
import { useRouter } from '../../../hooks/useRouter';
import { useTextSearch } from '../../../hooks/useTextSearch';
import { ChatChannelType } from '../../../models/ChatChannel';
import { Button, ButtonSize } from '../../../components/Button';
import { ButtonVariant } from '../../../config/colors';
import { useTheme } from '../../../hooks/useTheme';
import { AuthenticationStatusGetStream } from '../../../store/slices/authSlice';
import { TitleBar } from '../../../components/TitleBar';
import { useStream } from '../../../hooks/useStream';

interface ChatListViewProps {
  
}

const ChatListView: React.FC<ChatListViewProps> = () => {
  const [selectedFilter, setSelectedFilter] = useState(ChatFilter.ALL);
  const {pleb} = useAuth();
  const {statusGetStream, nbUnread} = useStream();
  const {navigateFromChannel} = useRouter();
  const { textSearch } = useTextSearch();
  const [filters, setFilters] = useState<ChannelFilters | null>(null);
  const [nbRefresh, setNbRefresh] = useState(0);
  const {isMobile} = useTheme();

  useEffect(() => {
    const setFiltersAsync = async() => {
      const filtersOr = [];

      const filters: ChannelFilters = {
        members: { $in: [pleb?.id || '', nbRefresh.toString()] },
        type: { $in : [ChatChannelType.Messaging, ChatChannelType.Event, ChatChannelType.Group]},
        disabled: {$eq: false}
      };

      if(pleb?.id === '634450c45a1df87a781ef269' || pleb?.id === '66ba8c56a2c151a6187fc6a2') { // Dirty code, if id == Matteo hide waiting for reply ones
        const queryFilters : ArrayTwoOrMore<ChannelFilters> = [
          {"status": { $eq: 'established' }},
          {"type" : {$in : [ChatChannelType.Event, ChatChannelType.Group]}},
          {"has_unread": {$eq: true}}
        ];
        
        filtersOr.push(queryFilters)
      }
    
      if (textSearch !== '') {
        const queryFilters : ArrayTwoOrMore<ChannelFilters> = [
          { 
            $and: [
              { "member.user.name": { $autocomplete: textSearch } },
              { "member_count": { $lte: 2 } },
            ],
          },
          { 
            $and: [
              { "name": { $autocomplete: textSearch } },
              { "member_count": { $gte: 2 } },
            ],
          },
        ];

        filtersOr.push(queryFilters)
      }
    
      switch(selectedFilter) {
        case ChatFilter.ALL:
          
          break;
        case ChatFilter.GROUPS:
          filters.type = { $eq: selectedFilter };
          break;
        case ChatFilter.EVENTS:
          filters.type = { $eq: selectedFilter };
          break;
        case ChatFilter.MERCHANTS:
          filters.merchantCount = { $eq: pleb?.merchant ? 2 : 1 };
          break;
      }

      if (filtersOr.length > 0) {
        if(filtersOr.length === 1)
          filters["$or"] = filtersOr[0];
        else
        {
          // @ts-ignore
          filters["$and"] = filtersOr.map((data) => {
            return {
              "$or": data
            }
          });
        }
      }

      setFilters(filters);
    }

    setFiltersAsync();
  }, [pleb, textSearch, selectedFilter, nbRefresh]);

  const options = { 
    //presence: true, 
    state: true, 
    watch: true,
    limit: 20
  };
  const sort: ChannelSort = [
    { 
      pinned_at: -1,
      last_updated: -1 
    }
  ];

  const PreviewWrapper = (props: any) => {
    return <ChatListTile 
      chatChannel={createChatChannelModel(props.channel, pleb?.id || '')}
      onTap={() => navigateFromChannel(props.channel)}
      isSelected={props.activeChannel?.id === props.channel.id}
    />
  };

  const renderLoadingErrorIndicator = (withRetry: boolean = true) => {
    return <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
      <div className="flex flex-col items-center justify-center">
        <p className="text-center text-base opacity-50">
          An error occured while loading the channels
        </p>
        {withRetry && <Button
          variant={ButtonVariant.primary}
          onClick={() => setNbRefresh(nbRefresh + 1)}
          size={ButtonSize.medium}
          className="mt-4"
        >
            <i className='material-icons'>refresh</i> Retry
          </Button>
        }
      </div>
    </div>
  }

  const renderChannels = (loadedChannels: Channel[], channelPreview: (item: Channel) => React.ReactNode) => {
    return removeDuplicatesById(loadedChannels)
      .map((channel) => channelPreview(channel))
  }
  
  const renderEmptyState = (selectedFilter: ChatFilter) => {  
    let message = `Nothing to see here yet, connect with some plebs and when they reply their messages will appear here`;

    switch(selectedFilter)
    {
      case ChatFilter.MERCHANTS:
        message = "Nothing to see here yet, connect with some merchants and when they reply their messages will appear here"
        break;
      case ChatFilter.EVENTS:
        message = "Nothing to see here yet, when you join an event group chat your messages will appear here"
        break;
      case ChatFilter.GROUPS:
        message = "Nothing to see here yet, when you join a group chat your messages will appear here"
        break;
    }

    if(textSearch !== '')
      message = "No results, try to search something else";

    return (
      <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
        <p className="text-center text-base opacity-50">
          {message}
        </p>
      </div>
    )
  }

  const renderChannelList = () => {
    if(statusGetStream === AuthenticationStatusGetStream.LOADING) return <Loader />;
    if(statusGetStream === AuthenticationStatusGetStream.ERROR) return renderLoadingErrorIndicator(false);
    if(statusGetStream === AuthenticationStatusGetStream.UNAUTHENTICATED) return <Loader />;

    if(!filters) return null;

    return <ChannelList 
      filters={filters} 
      options={options} 
      sort={sort} 
      Preview={(props: any) => PreviewWrapper(props)}
      EmptyStateIndicator={() => renderEmptyState(selectedFilter)}
      LoadingIndicator={() => <Loader />}
      setActiveChannelOnMount={false}
      renderChannels={renderChannels}
      LoadingErrorIndicator={() => renderLoadingErrorIndicator(true)}
      Paginator={InfiniteScroll}
    />
  };

  return (
    <div className="flex-1 pt-8 overflow-hidden flex flex-col gap-8">
      <TitleBar className="px-4" title="Chat" badgeMobile={nbUnread} />

      <div className="flex flex-col overflow-hidden">
        <ChatListViewHeader 
          selectedFilter={selectedFilter} 
          handleFilterChange={setSelectedFilter}
        />
        <div className="flex-1 overflow-hidden">
          {renderChannelList()}
        </div>
      </div>
    </div>
  );
};

export default memo(ChatListView);

const removeDuplicatesById = (arr: any[]) => {
  const seenIds = new Set();
  return arr.filter((obj: any) => !seenIds.has(obj.id) && seenIds.add(obj.id));
};
